import type { HTMLAttributes, ReactElement, ReactNode } from "react";
import styles from "./Callout.module.css";

export type CalloutTone = "info" | "success" | "warning" | "danger";

// `title` is deliberately omitted from the underlying attributes: a callout
// already states its situation in visible words, so the native tooltip would
// only duplicate it — and the prop below is the richer, rendered headline.
export interface CalloutProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  tone?: CalloutTone;
  /** Short headline. Rendered as a `<strong>`, not a heading — a callout must not pollute the document outline. */
  title?: ReactNode;
}

/**
 * An inline message block: a sign-in failure, a fetch error, the
 * configuration-error screen's explanation.
 *
 * Defaults to a polite live region so a message that appears after load is
 * announced without interrupting; pass `role` to override. Tone is
 * reinforcement only — the message text always states the situation in words,
 * never colour alone (WCAG 1.4.1).
 *
 * Never render a raw error object, a token, or a claim set inside a Callout —
 * an operator-facing message is plain language (ISSUE-329 §Log statements).
 */
export function Callout({
  tone = "info",
  title,
  className,
  children,
  ...rest
}: CalloutProps): ReactElement {
  const classes = [styles.callout, styles[tone], className].filter(Boolean).join(" ");
  return (
    <div className={classes} role="status" {...rest}>
      {title !== undefined ? <strong className={styles.title}>{title}</strong> : null}
      <div className={styles.body}>{children}</div>
    </div>
  );
}
